import type { TimelineFrame } from './RecordingManager';

export type PlaybackMode = 'live' | 'playback';
export type PlaybackSpeed = 0.1 | 0.25 | 0.5 | 1.0 | 2.0;

/**
 * PlaybackController - Timeline Playback Control
 * 
 * Manages playback of recorded animation sessions with speed control,
 * scrubbing, and frame stepping.
 */
export class PlaybackController {
  private mode: PlaybackMode = 'live';
  private isPlaying: boolean = false;
  private currentTime: number = 0; // milliseconds
  private playbackSpeed: PlaybackSpeed = 1.0;
  private looping: boolean = false;
  private frames: TimelineFrame[] = [];
  private lastUpdateTime: number = 0;

  /**
   * Switch to playback mode with provided frames
   */
  enterPlaybackMode(frames: TimelineFrame[]): void {
    this.mode = 'playback';
    this.frames = frames;
    this.currentTime = 0;
    this.isPlaying = false;
    this.lastUpdateTime = performance.now();
  }

  /**
   * Switch back to live mode
   */
  enterLiveMode(): void {
    this.mode = 'live';
    this.frames = [];
    this.currentTime = 0;
    this.isPlaying = false;
  }

  /**
   * Start playback
   */
  play(): void {
    if (this.mode !== 'playback') return;
    this.isPlaying = true;
    this.lastUpdateTime = performance.now();
  }

  /**
   * Pause playback
   */
  pause(): void {
    this.isPlaying = false;
  }

  /**
   * Toggle play/pause
   */
  togglePlayPause(): void {
    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }

  /**
   * Seek to specific time in milliseconds
   */
  seekTo(timeMs: number): void {
    if (this.mode !== 'playback' || this.frames.length === 0) return;
    
    const maxTime = this.frames[this.frames.length - 1].timestamp;
    this.currentTime = Math.max(0, Math.min(timeMs, maxTime));
    this.lastUpdateTime = performance.now();
  }

  /**
   * Seek to normalized time (0-1)
   */
  seekToNormalized(normalizedTime: number): void {
    if (this.mode !== 'playback' || this.frames.length === 0) return;
    
    const maxTime = this.frames[this.frames.length - 1].timestamp;
    const timeMs = normalizedTime * maxTime;
    this.seekTo(timeMs);
  }

  /**
   * Step forward one frame
   */
  stepForward(): void {
    if (this.mode !== 'playback' || this.frames.length === 0) return;
    
    const currentFrame = this.getCurrentFrameIndex();
    if (currentFrame < this.frames.length - 1) {
      this.currentTime = this.frames[currentFrame + 1].timestamp;
    }
  }

  /**
   * Step backward one frame
   */
  stepBackward(): void {
    if (this.mode !== 'playback' || this.frames.length === 0) return;
    
    const currentFrame = this.getCurrentFrameIndex();
    if (currentFrame > 0) {
      this.currentTime = this.frames[currentFrame - 1].timestamp;
    }
  }

  /**
   * Set playback speed
   */
  setSpeed(speed: PlaybackSpeed): void {
    this.playbackSpeed = speed;
  }

  /**
   * Set looping
   */
  setLooping(loop: boolean): void {
    this.looping = loop;
  }

  /**
   * Update playback (call every frame)
   */
  update(): void {
    if (!this.isPlaying || this.mode !== 'playback' || this.frames.length === 0) {
      return;
    }

    const now = performance.now();
    const deltaMs = (now - this.lastUpdateTime) * this.playbackSpeed;
    this.lastUpdateTime = now;

    this.currentTime += deltaMs;

    // Handle end of timeline
    const maxTime = this.frames[this.frames.length - 1].timestamp;
    if (this.currentTime >= maxTime) {
      if (this.looping) {
        this.currentTime = 0;
      } else {
        this.currentTime = maxTime;
        this.isPlaying = false;
      }
    }
  }

  /**
   * Get frame at current playback time
   */
  getCurrentFrame(): TimelineFrame | null {
    if (this.frames.length === 0) return null;
    
    const index = this.getCurrentFrameIndex();
    return this.frames[index] || null;
  }

  /**
   * Get frame at specific time
   */
  getFrameAtTime(timeMs: number): TimelineFrame | null {
    if (this.frames.length === 0) return null;

    // Binary search for closest frame
    let left = 0;
    let right = this.frames.length - 1;
    
    while (left < right) {
      const mid = Math.floor((left + right) / 2);
      if (this.frames[mid].timestamp < timeMs) {
        left = mid + 1;
      } else {
        right = mid;
      }
    }

    return this.frames[left] || this.frames[0];
  }

  /**
   * Get current frame index
   */
  private getCurrentFrameIndex(): number {
    if (this.frames.length === 0) return 0;

    // Binary search for closest frame
    let left = 0;
    let right = this.frames.length - 1;
    
    while (left < right) {
      const mid = Math.floor((left + right) / 2);
      if (this.frames[mid].timestamp < this.currentTime) {
        left = mid + 1;
      } else {
        right = mid;
      }
    }

    return left;
  }

  /**
   * Get playback state
   */
  getState() {
    return {
      mode: this.mode,
      isPlaying: this.isPlaying,
      currentTime: this.currentTime,
      speed: this.playbackSpeed,
      looping: this.looping,
      duration: this.frames.length > 0 
        ? this.frames[this.frames.length - 1].timestamp 
        : 0,
      frameCount: this.frames.length,
    };
  }

  /**
   * Get current mode
   */
  getMode(): PlaybackMode {
    return this.mode;
  }

  /**
   * Get current time
   */
  getCurrentTime(): number {
    return this.currentTime;
  }

  /**
   * Get normalized playback position (0-1)
   */
  getNormalizedPosition(): number {
    if (this.frames.length === 0) return 0;
    const maxTime = this.frames[this.frames.length - 1].timestamp;
    return maxTime > 0 ? this.currentTime / maxTime : 0;
  }
}
